import React from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { cn } from "../../lib/cn";

/**
 * claudesign Dialog — thin Radix wrapper at MUI Dialog/Modal parity.
 * Chrome mirrors docs/redesign/guest-system.css `.dialog` / `.dlg-dim`.
 * Supersedes ad-hoc MUI <Dialog>/<Modal> usage across components/Modal/*.
 *
 * Compound API — callers own the inner padded body + actions:
 *   <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
 *     <Dialog.Content className="max-w-[480px]">
 *       <Dialog.Title>Delete track?</Dialog.Title>
 *       ...body / actions...
 *     </Dialog.Content>
 *   </Dialog.Root>
 *
 * Radix gives focus-trap, scroll-lock, Esc + overlay-click close, and aria wiring.
 * Radix requires a <Dialog.Title> (or aria-label) for a11y; pass
 * aria-describedby={undefined} on Content when there is no <Dialog.Description>.
 */
export const Root = RadixDialog.Root;
export const Trigger = RadixDialog.Trigger;
export const Close = RadixDialog.Close;

export const Content = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Content>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Content>
>(({ className, children, ...props }, ref) => (
  <RadixDialog.Portal>
    <RadixDialog.Overlay className="fixed inset-0 z-[1400] bg-overlay" />
    <RadixDialog.Content
      ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-[1401] -translate-x-1/2 -translate-y-1/2",
        "w-[calc(100%-32px)] max-w-[480px]",
        "border border-border bg-surface rounded-lg overflow-hidden",
        "shadow-[0_50px_130px_-45px_rgba(0,0,0,.9)] focus:outline-none",
        className,
      )}
      {...props}
    >
      {children}
    </RadixDialog.Content>
  </RadixDialog.Portal>
));
Content.displayName = "DialogContent";

export const Title = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Title>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(({ className, ...props }, ref) => (
  <RadixDialog.Title
    ref={ref}
    className={cn("text-[23px] font-bold tracking-[-0.015em] text-white", className)}
    {...props}
  />
));
Title.displayName = "DialogTitle";

export const Description = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Description>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(({ className, ...props }, ref) => (
  <RadixDialog.Description
    ref={ref}
    className={cn("text-[14.5px] leading-[1.55] text-muted", className)}
    {...props}
  />
));
Description.displayName = "DialogDescription";

/** Namespaced object export, mirroring how callers use Radix compound components. */
export const Dialog = { Root, Trigger, Close, Content, Title, Description };
export default Dialog;
